import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, Clock, Instagram, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { MaskedLines, FadeUp } from "../components/motion";
import SEO from "../components/SEO";
import { INSTAGRAM_URL, WHATSAPP_URL } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-ink/20 bg-cream px-4 py-3.5 text-sm text-ink placeholder:text-stone2/50 focus:border-rouge focus:outline-none transition-colors duration-300";

const ContactForm = () => {
  const { t } = useLanguage();
  const f = t.contact.form;
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
    } catch {
      toast.error(f.error);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="border border-ink/15 bg-sand p-10 lg:p-14"
        data-testid="contact-success"
      >
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-rouge text-cream">
          <Check size={24} />
        </span>
        <h3 className="mt-8 font-display text-3xl font-bold">{f.successTitle}</h3>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-stone2">{f.successText.replace("{name}", form.name)}</p>
        <button
          onClick={() => { setDone(false); setForm({ name: "", email: "", message: "" }); }}
          data-testid="contact-again-button"
          className="mt-10 border border-ink px-7 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-cream"
        >
          {f.again}
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-6" data-testid="contact-form">
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="ct-name" className="overline-label mb-2.5 block text-stone2">{f.name}</label>
          <input id="ct-name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" autoComplete="name" />
        </div>
        <div>
          <label htmlFor="ct-email" className="overline-label mb-2.5 block text-stone2">{f.email}</label>
          <input id="ct-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" autoComplete="email" />
        </div>
      </div>
      <div>
        <label htmlFor="ct-message" className="overline-label mb-2.5 block text-stone2">{f.message}</label>
        <textarea id="ct-message" required minLength={5} rows={6} value={form.message} onChange={set("message")} placeholder={f.messagePlaceholder} className={`${inputCls} resize-none`} data-testid="contact-message-input" />
      </div>
      <div>
        <button
          type="submit"
          disabled={loading}
          data-testid="contact-submit-button"
          className="group inline-flex items-center gap-3 bg-rouge px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-rouge-dark disabled:opacity-60"
        >
          {loading ? f.sending : f.submit}
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </form>
  );
};

const Contact = () => {
  const { t } = useLanguage();
  return (
    <>
      <SEO page="contact" />
      <section className="mx-auto max-w-[1400px] px-6 pt-40 lg:px-12 lg:pt-48" data-testid="contact-header">
        <p className="overline-label text-rouge">{t.contact.overline}</p>
        <h1 className="mt-8 font-display text-5xl font-bold leading-[1.02] tracking-tighter sm:text-6xl lg:text-7xl">
          <MaskedLines
            inView
            lines={[
              <span>{t.contact.title}</span>,
              <span className="italic font-medium text-rouge">{t.contact.titleItalic}</span>,
            ]}
          />
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-stone2 md:text-lg">{t.contact.sub}</p>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-32" data-testid="contact-content">
        <div className="grid gap-20 lg:grid-cols-12">
          <div className="space-y-12 lg:col-span-5">
            <FadeUp className="border-t border-ink/10 pt-8">
              <p className="overline-label flex items-center gap-2 text-stone2"><MapPin size={14} className="text-rouge" /> {t.contact.addressTitle}</p>
              <p className="mt-4 font-display text-2xl font-bold" data-testid="contact-address">{t.contact.address}</p>
              <p className="mt-2 text-sm text-stone2">{t.contact.addressNote}</p>
            </FadeUp>
            <FadeUp delay={0.1} className="border-t border-ink/10 pt-8">
              <p className="overline-label flex items-center gap-2 text-stone2"><Clock size={14} className="text-rouge" /> {t.contact.hoursTitle}</p>
              <dl className="mt-4 space-y-3" data-testid="contact-hours">
                {t.contact.hours.map((h) => (
                  <div key={h.day} className="flex items-baseline justify-between gap-6 text-sm">
                    <dt className="font-semibold">{h.day}</dt>
                    <dd className="text-stone2">{h.time}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-6 text-sm text-stone2">{t.contact.regionTitle}: {t.contact.regionText}</p>
            </FadeUp>
            <FadeUp delay={0.2} className="border-t border-ink/10 pt-8">
              <div className="flex flex-wrap gap-3">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-instagram-button"
                  className="inline-flex items-center gap-2 border border-ink px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-cream"
                >
                  <Instagram size={15} /> Instagram
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="inline-flex items-center gap-2 border border-rouge bg-rouge px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rouge-dark"
                >
                  <MessageCircle size={15} /> WhatsApp
                </a>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-7">
            <ContactForm />
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Contact;
